import { useFrame } from '@react-three/fiber';
import { ECS } from '../state';
import {
  players,
  clouds,
  characters,
  collectibles,
  obstacles,
  endings,
} from '../queries';
import {
  addCloud,
  addCharacter,
  addCollectible,
  addObstacle,
  addEndings,
} from '../entities';
import {
  beerConfig,
  OBSTACLE_GAP_MIN,
  OBSTACLE_GAP_MAX,
  COLLECTIBLE_GAP_MIN,
  COLLECTIBLE_GAP_MAX,
  // ITEM_GAP_MIN,
  // ITEM_GAP_MAX,
  CLOUD_GAP_MIN,
  CLOUD_GAP_MAX,
  CHARACTER_GAP_MIN,
  CHARACTER_GAP_MAX,
  itemSizes,
  SPAWN_DISTANCE,
  SCORE_LIMIT,
  ENDING_DISTANCE,
} from '@/config/games/beer';
import { randomInRange, randomIntRange, randomSign } from '~/helpers/MathUtils';
import sample from 'lodash/sample';

// Spawning state
let lastSpawnX = 0;
let lastCloudSpawnX = 0;
let lastCharacterSpawnX = 0;
let lastObstacleSpawnX = 0;
let lastCollectibleSpawnX = 0;
let lastItemSpawnX = 0;
let consecutiveObstacles = 0; // Track consecutive obstacles spawned
let consecutiveCollectibles = 0; // Track consecutive collectibles spawned

let cleanupDistance = 50; // Distance behind player to clean up objects
let initialized = false;
let endingSpawned = false; // Track if ending has been spawned

export const SystemSpawn = ({ stage = 0 }) => {
  const score = useScore();

  useFrame(() => {
    const player = players.entities[0];
    if (!player) return;

    // Initialize spawn positions on first frame
    if (!initialized) {
      lastCloudSpawnX = player.position.x;
      lastCharacterSpawnX = player.position.x;
      lastObstacleSpawnX = player.position.x;
      lastCollectibleSpawnX = player.position.x;
      lastItemSpawnX = player.position.x;
      initialized = true;
    }

    const playerX = player.position.x;
    const targetSpawnX = playerX + SPAWN_DISTANCE;

    // Determine current area based on player position
    // You can adjust this logic based on your game's area transitions
    // currentArea = Math.floor(playerX / 100) % beerConfig.areas.length;

    // Check if score has reached limit and ending hasn't been spawned yet
    // if (score >= SCORE_LIMIT && !endingSpawned) {
    //   // Remove obstacles and collectibles that are in front of the player and not visible
    //   cleanupObjectsInFront(playerX);

    //   // Add ending items for the current area
    //   addEndings(currentArea, playerX + ENDING_DISTANCE); // Spawn ending items 20 units ahead of player

    //   endingSpawned = true;
    //   return; // Stop normal spawning once ending is triggered
    // }

    // CLEAN UP
    // clean up objects far behind the player
    cleanupObjects(playerX - cleanupDistance);

    // SPAWN
    // Only continue with normal spawning if ending hasn't been triggered
    if (score >= SCORE_LIMIT) return;

    // Spawn clouds
    if (targetSpawnX > lastCloudSpawnX) {
      const area = beerConfig.areas[stage];
      const gap = randomInRange(CLOUD_GAP_MIN, CLOUD_GAP_MAX);
      lastCloudSpawnX += gap;

      const y = randomInRange(1.75, 9);
      const z = randomInRange(-20, -5);
      const type = randomIntRange(0, 4);
      const scale = randomInRange(1, 2);

      addCloud(lastCloudSpawnX, y, z, 2 * scale, 1 * scale, type);
    }

    // Spawn characters
    if (targetSpawnX > lastCharacterSpawnX) {
      const area = beerConfig.areas[stage];
      const gap = randomInRange(CHARACTER_GAP_MIN, CHARACTER_GAP_MAX);
      lastCharacterSpawnX += gap;

      const y = 0;
      const z = randomInRange(area.characterRangeZ[0], area.characterRangeZ[1]);
      const type = sample(area.characters);
      const scale = area.characterScale;
      const flip = randomSign();

      addCharacter(lastCharacterSpawnX, y, z, scale, scale, type, flip);
    }

    // Spawn items (obstacles or collectibles) using ITEM_GAP_MIN and ITEM_GAP_MAX
    if (targetSpawnX > lastItemSpawnX) {
      const area = beerConfig.areas[stage];
      const gap = randomInRange(area.itemGap.min, area.itemGap.max);
      lastItemSpawnX += gap;

      // Determine what to spawn based on consecutive items rule
      let shouldSpawnObstacle = Math.random() < 0.6; // 60% chance for obstacle, 40% for collectible

      // Force collectible if we've spawned 3 obstacles in a row
      if (consecutiveObstacles >= 3) {
        shouldSpawnObstacle = false;
      }

      // Force obstacle if we've spawned 1 collectible in a row
      if (consecutiveCollectibles >= 1) {
        shouldSpawnObstacle = true;
      }

      if (shouldSpawnObstacle && area.obstacles.length > 0) {
        // Spawn obstacle
        const obstacleConfig = sample(area.obstacles);
        const type = obstacleConfig.type;
        const y = randomInRange(
          obstacleConfig.height?.min || 0,
          obstacleConfig.height?.max || 0
        );
        const size = itemSizes[type];
        const width = size[0];
        const height = size[1];

        addObstacle(
          lastItemSpawnX,
          y,
          width,
          height,
          type,
          obstacleConfig.opts
        );
        consecutiveObstacles++; // Increment consecutive obstacles counter
        consecutiveCollectibles = 0; // Reset consecutive collectibles counter when obstacle is spawned
      } else if (area.collectibles.length > 0) {
        // Spawn collectible
        const collectibleConfig = sample(area.collectibles);
        const type = collectibleConfig.type;
        const size = itemSizes[type];
        const width = size[0];
        const height = size[1];

        const y = randomInRange(
          collectibleConfig.height?.min || 0,
          collectibleConfig.height?.max || 0
        );

        // Only spawn collectible if conditions are met
        addCollectible(
          lastItemSpawnX,
          y,
          width,
          height,
          type,
          collectibleConfig.opts
        );
        consecutiveObstacles = 0; // Reset consecutive obstacles counter when collectible is spawned
        consecutiveCollectibles++; // Increment consecutive collectibles counter
      }
    }

    // // Spawn obstacles
    // if (targetSpawnX > lastObstacleSpawnX) {
    //   const area = beerConfig.areas[stage];
    //   const obstacleConfig = sample(area.obstacles);
    //   const type = obstacleConfig.type;
    //   const y = randomInRange(
    //     obstacleConfig.height?.min || 0,
    //     obstacleConfig.height?.max || 0
    //   );
    //   const size = itemSizes[type];
    //   const width = size[0];
    //   const height = size[1];

    //   lastObstacleSpawnX += width;
    //   lastObstacleSpawnX += randomInRange(OBSTACLE_GAP_MIN, OBSTACLE_GAP_MAX);

    //   addObstacle(
    //     lastObstacleSpawnX,
    //     y,
    //     width,
    //     height,
    //     type,
    //     obstacleConfig.opts
    //   );
    // }

    // // Spawn collectibles
    // if (targetSpawnX > lastCollectibleSpawnX) {
    //   const area = beerConfig.areas[stage];
    //   const collectibleConfig = sample(area.collectibles);
    //   const type = collectibleConfig.type;
    //   const size = itemSizes[type];
    //   const width = size[0];
    //   const height = size[1];

    //   lastCollectibleSpawnX += width;
    //   lastCollectibleSpawnX += randomInRange(
    //     COLLECTIBLE_GAP_MIN,
    //     COLLECTIBLE_GAP_MAX
    //   );

    //   // Check if there's an obstacle behind that would require jumping
    //   const obstacleCheckDistance = 1.5; // Distance to check for obstacles behind
    //   let collectibleY = randomInRange(
    //     collectibleConfig.height?.min || 0,
    //     collectibleConfig.height?.max || 0
    //   ); // Default height

    //   if (collectibleConfig.canSpawnAboveObstacle) {
    //     for (const obstacle of obstacles) {
    //       // Only check obstacles that are behind the collectible spawn position
    //       if (obstacle.position.x < lastCollectibleSpawnX) {
    //         const distance = lastCollectibleSpawnX - obstacle.position.x;
    //         if (distance <= obstacleCheckDistance) {
    //           // If obstacle is close behind, spawn collectible higher so player needs to jump on obstacle first
    //           collectibleY = Math.max(
    //             collectibleY,
    //             obstacle.position.y + obstacle.size[1] + collectibleY
    //           );
    //         }
    //       }
    //     }
    //   }

    //   if (stage == 2) {
    //     for (const obstacle of obstacles) {
    //       if (obstacle.position.x < lastCollectibleSpawnX) {
    //         const distance = Math.abs(
    //           lastCollectibleSpawnX - obstacle.position.x
    //         );
    //         if (distance <= 2) {
    //           lastCollectibleSpawnX += 3;
    //         }
    //       }
    //     }
    //   }

    //   // Only spawn collectible if conditions are met
    //   addCollectible(
    //     lastCollectibleSpawnX,
    //     collectibleY,
    //     width,
    //     height,
    //     type,
    //     collectibleConfig.opts
    //   );
    // }
  });

  return null;
};

// Cleanup function to remove objects that are far behind the player
const cleanupObjects = (cleanupX) => {
  // Clean up clouds
  for (const cloud of clouds) {
    if (cloud.position.x < cleanupX) {
      ECS.world.remove(cloud);
    }
  }

  // Clean up characters
  for (const character of characters) {
    if (character.position.x < cleanupX) {
      ECS.world.remove(character);
    }
  }

  // Clean up obstacles
  for (const obstacle of obstacles) {
    if (obstacle.position.x < cleanupX) {
      ECS.world.remove(obstacle);
    }
  }

  // Clean up collectibles
  for (const collectible of collectibles) {
    if (collectible.position.x < cleanupX) {
      ECS.world.remove(collectible);
    }
  }
};

// Cleanup function to remove obstacles and collectibles in front of player that are not visible
export const cleanupObjectsInFront = (playerX) => {
  // Clean up obstacles in front of player
  for (const obstacle of obstacles) {
    if (obstacle.position.x > playerX + ENDING_DISTANCE / 2) {
      ECS.world.remove(obstacle);
    }
  }

  // Clean up collectibles in front of player
  for (const collectible of collectibles) {
    if (collectible.position.x > playerX + ENDING_DISTANCE / 2) {
      ECS.world.remove(collectible);
    }
  }
};

// Reset function to clear spawning state (useful for game restart)
export const resetSpawnSystem = () => {
  lastSpawnX = 0;
  lastCloudSpawnX = 0;
  lastCharacterSpawnX = 0;
  lastObstacleSpawnX = 0;
  lastCollectibleSpawnX = 0;
  lastItemSpawnX = 0;
  consecutiveObstacles = 0;
  consecutiveCollectibles = 0;
  initialized = false;
  endingSpawned = false;
};
